import {
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  Properties,
  simpleHeader,
  withColumnGutters,
} from "@homebound/beam";
import { Link } from "react-router-dom";
import { MarkupSummary } from "src/components/MarkupSummary";
import { Price } from "src/components/Price";
import { ChangeEventLineItemType } from "src/generated/graphqlTypes";
import { CostSourceType } from "src/routes/projects/change-events/ChangeEventLineItemsTable";
import {
  ScopeChangesTable_ChangeEventFragment,
  ScopeChangesTable_ChangeEventLineItemFragment,
} from "src/routes/projects/change-events/guided/components/ScopeChangesTable.generated";
import { createProjectBidContractOverviewUrl } from "src/RouteUrls";
import { isDefined } from "src/utils";
import { emptyCellDash } from "src/utils/constants";
import { tradeName } from "../utils";

type ScopeChangesTableProps = { changeEvent: ScopeChangesTable_ChangeEventFragment };

export function ScopeChangesTable(props: ScopeChangesTableProps) {
  const { changeEvent } = props;
  return (
    <GridTable
      id="scopeChangesTable"
      columns={withColumnGutters(createColumns(changeEvent.project.id))}
      rows={createRows(changeEvent)}
      fallbackMessage="This change event has no line items yet."
    />
  );
}

type HeaderRow = { kind: "header"; id: string; data: undefined };
type CostCodeRow = { kind: "costCode"; id: string; data: string };
type LineItemRow = { kind: "celi"; id: string; data: ScopeChangesTable_ChangeEventLineItemFragment };
type Row = HeaderRow | CostCodeRow | LineItemRow;

function createRows(changeEvent: ScopeChangesTable_ChangeEventFragment): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...changeEvent.lineItems
      .groupByObject((li) => li.item.costCode)
      .sortBy(([costCode]) => costCode.displayName)
      .map(([costCode, celis]) => ({
        kind: "costCode" as const,
        id: costCode.id,
        data: costCode.displayName,
        children: sortCelis(celis).map((celi) => ({ kind: "celi" as const, id: celi.id, data: celi })),
      })),
  ];
}

function createColumns(projectId: string): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({ celi: emptyCell, w: "32px", sticky: "left" }),
    column<Row>({
      header: "Item",
      costCode: (displayName) => ({ content: displayName, css: Css.smSb.$ }),
      celi: (li) => li.item.displayName,
      w: 2,
    }),
    column<Row>({ header: "Line Item", costCode: emptyCell, celi: (li) => li.name, w: 2 }),
    column<Row>({
      header: "Type",
      costCode: emptyCell,
      celi: (li) => ({
        value: li.typeDetail.code,
        content: () => <span css={typeColor[li.typeDetail.code as ChangeEventLineItemType]}>{li.typeDetail.name}</span>,
      }),
    }),
    numericColumn<Row>({
      header: "Qty",
      costCode: emptyCell,
      celi: (li) => {
        if (li.unitOfMeasure?.useQuantity === false) return "N/A";
        if (!isDefined(li.quantity)) return emptyCellDash;
        return li.quantity > 0 ? `+${li.quantity}` : `${li.quantity}`;
      },
    }),
    column<Row>({ header: "UoM", costCode: emptyCell, celi: (li) => li.unitOfMeasure.abbreviation }),
    numericColumn<Row>({
      header: "Cost",
      costCode: emptyCell,
      celi: (li) => <Price valueInCents={li.totalCostInCents} displayDirection />,
    }),
    numericColumn<Row>({
      header: "Markup",
      costCode: emptyCell,
      celi: (li) => <MarkupSummary budget={li.totalCostInCents} price={li.totalPriceInCents} />,
    }),
    numericColumn<Row>({
      header: "Price",
      costCode: emptyCell,
      celi: (li) => <Price valueInCents={li.totalPriceInCents} displayDirection />,
    }),
    column<Row>({
      header: "Cost Source",
      costCode: emptyCell,
      celi: (li) => {
        const bcli = li.proposedBidContractLineItem;
        if (!bcli) return CostSourceType.MANUAL;
        return {
          value: CostSourceType.BID_CONTRACT,
          content: () => (
            <Link to={createProjectBidContractOverviewUrl(projectId, bcli.revision.id)}>
              {CostSourceType.BID_CONTRACT}
            </Link>
          ),
        };
      },
    }),
    column<Row>({ header: "Trade", costCode: emptyCell, celi: (li) => tradeName(li), w: "200px" }),
    column<Row>({
      header: "Task Allocation",
      costCode: emptyCell,
      celi: (li) => {
        const { originalTask, proposedTask } = li;
        return proposedTask?.name ?? originalTask?.name ?? emptyCellDash;
      },
    }),
  ];
}

/** The design colors each type by what it does to the project's scope. */
const typeColor: Record<ChangeEventLineItemType, Properties> = {
  [ChangeEventLineItemType.Add]: Css.green500.$,
  [ChangeEventLineItemType.Remove]: Css.red600.$,
  [ChangeEventLineItemType.Modify]: Css.orange500.$,
  [ChangeEventLineItemType.Cutoff]: Css.gray700.$,
};

const typeOrder: Record<ChangeEventLineItemType, number> = {
  [ChangeEventLineItemType.Add]: 0,
  [ChangeEventLineItemType.Remove]: 1,
  [ChangeEventLineItemType.Modify]: 2,
  [ChangeEventLineItemType.Cutoff]: 3,
};

function sortCelis(
  celis: ScopeChangesTable_ChangeEventLineItemFragment[],
): ScopeChangesTable_ChangeEventLineItemFragment[] {
  return celis.sortBy((celi) => typeOrder[celi.typeDetail.code as ChangeEventLineItemType] ?? Number.MAX_SAFE_INTEGER);
}
